<template>
  <section class="bg-white border border-slate-100 rounded-2xl shadow-sm p-6">
    <div class="flex items-center gap-2 mb-5">
      <div class="w-7 h-7 bg-blue-50 rounded-lg flex items-center justify-center">
        <svg class="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
          <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
        </svg>
      </div>
      <h2 class="text-base font-bold text-slate-800">New Booking</h2>
    </div>

    <form @submit.prevent="createBooking" class="space-y-4">

      <!-- ── Map section ── -->
      <div>
        <!-- Header row -->
        <div class="flex items-center justify-between mb-2">
          <label class="field-label mb-0">Select pickup &amp; destination</label>
          <button
            v-if="pickup || destination"
            type="button"
            @click="resetPoints"
            class="flex items-center gap-1 text-xs font-medium text-slate-400 hover:text-slate-600 transition-colors"
          >
            <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
            </svg>
            Clear both
          </button>
        </div>

        <!-- Step instruction -->
        <div
          class="flex items-center gap-1.5 mb-2.5 text-xs font-semibold"
          :class="pickup && destination ? 'text-emerald-600' : 'text-slate-500'"
        >
          <svg v-if="pickup && destination" class="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/>
          </svg>
          <svg v-else class="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5"/>
          </svg>
          <span v-if="!pickup">Step 1 — click the map to place your <strong>pickup</strong> pin</span>
          <span v-else-if="!destination">Step 2 — click the map to place your <strong>destination</strong> pin</span>
          <span v-else>Both pins placed — you're ready to book</span>
        </div>

        <!-- Map container -->
        <div style="height:300px; border-radius:12px; overflow:hidden; border:1px solid #e2e8f0; position:relative; z-index:0;">
          <div ref="mapContainer" style="height:100%; width:100%;"></div>
        </div>

        <!-- Location summary cards -->
        <div class="mt-2.5 grid grid-cols-2 gap-2">
          <!-- Pickup -->
          <div
            class="flex items-start gap-2.5 px-3 py-2.5 rounded-xl border text-xs transition-colors"
            :class="pickup ? 'bg-blue-50 border-blue-200' : 'bg-slate-50 border-slate-200'"
          >
            <span class="w-2.5 h-2.5 rounded-full flex-shrink-0 mt-0.5"
              :class="pickup ? 'bg-blue-500' : 'bg-slate-300'"></span>
            <div class="min-w-0 flex-1">
              <div class="flex items-center justify-between mb-0.5">
                <p class="font-semibold text-slate-600">Pickup</p>
                <button
                  v-if="pickup"
                  type="button"
                  @click.stop="clearPickup"
                  class="text-slate-300 hover:text-red-400 transition-colors ml-1 flex-shrink-0"
                  title="Clear pickup"
                >
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>

              <!-- Geocoding in progress -->
              <div v-if="geocodingPickup" class="flex items-center gap-1.5 text-slate-400">
                <svg class="w-3 h-3 animate-spin flex-shrink-0" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                </svg>
                <span>Looking up…</span>
              </div>

              <!-- Resolved -->
              <template v-else-if="pickup">
                <p class="text-slate-800 font-medium leading-snug break-words">{{ pickup.name }}</p>
                <p class="font-mono text-slate-400 mt-0.5">{{ pickup.lat.toFixed(5) }}, {{ pickup.lng.toFixed(5) }}</p>
              </template>

              <p v-else class="text-slate-400">Not placed</p>
            </div>
          </div>

          <!-- Destination -->
          <div
            class="flex items-start gap-2.5 px-3 py-2.5 rounded-xl border text-xs transition-colors"
            :class="destination ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'"
          >
            <span class="w-2.5 h-2.5 rounded-full flex-shrink-0 mt-0.5"
              :class="destination ? 'bg-emerald-500' : 'bg-slate-300'"></span>
            <div class="min-w-0 flex-1">
              <div class="flex items-center justify-between mb-0.5">
                <p class="font-semibold text-slate-600">Destination</p>
                <button
                  v-if="destination"
                  type="button"
                  @click.stop="clearDestination"
                  class="text-slate-300 hover:text-red-400 transition-colors ml-1 flex-shrink-0"
                  title="Clear destination"
                >
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>

              <!-- Geocoding in progress -->
              <div v-if="geocodingDest" class="flex items-center gap-1.5 text-slate-400">
                <svg class="w-3 h-3 animate-spin flex-shrink-0" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                </svg>
                <span>Looking up…</span>
              </div>

              <!-- Resolved -->
              <template v-else-if="destination">
                <p class="text-slate-800 font-medium leading-snug break-words">{{ destination.name }}</p>
                <p class="font-mono text-slate-400 mt-0.5">{{ destination.lat.toFixed(5) }}, {{ destination.lng.toFixed(5) }}</p>
              </template>

              <p v-else class="text-slate-400">Not placed</p>
            </div>
          </div>
        </div>
      </div>
      <!-- ── /Map section ── -->

      <!-- Date & time -->
      <div>
        <label class="field-label">Date &amp; time</label>
        <input v-model="form.bookingDateTime" type="datetime-local" class="field-input" required/>
      </div>

      <!-- Passengers + cab type -->
      <div class="grid grid-cols-2 gap-3">
        <div>
          <label class="field-label">Passengers</label>
          <input
            v-model.number="form.numberOfPassengers"
            type="number" min="1" max="8"
            class="field-input"
            placeholder="1–8"
            required
          />
        </div>
        <div>
          <label class="field-label">Cab type</label>
          <select v-model="form.cabType" class="field-input" required>
            <option value="Economic">Economic</option>
            <option value="Premium">Premium</option>
            <option value="Executive">Executive</option>
          </select>
        </div>
      </div>

      <AlertBanner :message="message" type="success" />
      <AlertBanner :message="error"   type="error" />

      <div class="flex gap-2">
        <button
          type="submit"
          :disabled="loading"
          class="flex-1 bg-blue-600 text-white py-2.5 rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          <svg v-if="loading" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
          </svg>
          {{ loading ? 'Creating…' : 'Create Booking' }}
        </button>
        <button
          type="button"
          @click="clearForm"
          class="flex items-center gap-1.5 border border-slate-200 text-slate-500 hover:text-slate-700 hover:bg-slate-50 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors"
          title="Clear form"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
          </svg>
          Clear
        </button>
      </div>
    </form>
  </section>
</template>

<script>
import 'leaflet/dist/leaflet.css'
import L from 'leaflet'
import AlertBanner from "@/components/AlertBanner.vue"

function makePinIcon(bgColor) {
  return L.divIcon({
    className: '',
    html: `<div style="
      width:18px; height:18px;
      background:${bgColor};
      border:3px solid #fff;
      border-radius:50%;
      box-shadow:0 2px 8px rgba(0,0,0,0.35);
    "></div>`,
    iconSize:      [18, 18],
    iconAnchor:    [9, 9],
    tooltipAnchor: [0, -11],
  })
}

const PICKUP_ICON = makePinIcon('#2563eb') // blue-600
const DEST_ICON   = makePinIcon('#16a34a') // green-600

async function reverseGeocode(lat, lng) {
  try {
    const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=en`
    const res = await fetch(url)
    if (!res.ok) return null

    const data = await res.json()
    const addr = data.address || {}

    // Build a short, readable name: prefer "Road, City" form
    const road = addr.road || addr.pedestrian || addr.path || addr.suburb || ''
    const city = addr.city || addr.town || addr.village || addr.hamlet || addr.county || ''

    if (road && city)  return `${road}, ${city}`
    if (city)          return city
    if (road)          return road

    // Last resort: first two comma-parts of the full display name
    const parts = (data.display_name || '').split(',').map(s => s.trim()).filter(Boolean)
    return parts.slice(0, 2).join(', ') || null
  } catch {
    return null
  }
}

export default {
  name: "BookingForm",
  components: { AlertBanner },
  emits: ["booking-created"],

  data() {
    return {
      message: "",
      error:   "",
      loading: false,

      // Map state
      map:             null,
      pickup:          null, // { lat, lng, name }
      destination:     null, // { lat, lng, name }
      pickupMarker:    null,
      destMarker:      null,
      geocodingPickup: false,
      geocodingDest:   false,

      form: {
        bookingDateTime:    "",
        numberOfPassengers: 2,
        cabType:            "Premium",
      },
    }
  },

  mounted() {
    this.$nextTick(() => this.initMap())
  },

  beforeUnmount() {
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  },

  methods: {
    initMap() {
      this.map = L.map(this.$refs.mapContainer, {
        center: [35.9, 14.45], // Malta
        zoom:   11,
      })

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a>',
        maxZoom: 19,
      }).addTo(this.map)

      this.map.on('click', this.onMapClick)
    },

    async onMapClick({ latlng }) {
      const { lat, lng } = latlng

      // ── Place pickup pin ──
      if (!this.pickup) {
        this.geocodingPickup = true
        // Store coords immediately so the summary card activates
        this.pickup = { lat, lng, name: null }

        this.pickupMarker = L.marker([lat, lng], { icon: PICKUP_ICON })
          .addTo(this.map)
          .bindTooltip('Pickup', {
            permanent:  true,
            direction:  'top',
            className:  'cb-tip cb-tip--blue',
          })

        const name = await reverseGeocode(lat, lng) ?? 'Selected pickup location'
        this.pickup = { lat, lng, name }

        // Update the map tooltip label to the resolved name
        this.pickupMarker.getTooltip()
          ?.setContent(name.length > 28 ? name.slice(0, 26) + '…' : name)

        this.geocodingPickup = false
        return
      }

      // ── Place destination pin ──
      if (!this.destination) {
        this.geocodingDest = true
        this.destination = { lat, lng, name: null }

        this.destMarker = L.marker([lat, lng], { icon: DEST_ICON })
          .addTo(this.map)
          .bindTooltip('Destination', {
            permanent:  true,
            direction:  'top',
            className:  'cb-tip cb-tip--green',
          })

        const name = await reverseGeocode(lat, lng) ?? 'Selected destination location'
        this.destination = { lat, lng, name }

        this.destMarker.getTooltip()
          ?.setContent(name.length > 28 ? name.slice(0, 26) + '…' : name)

        this.geocodingDest = false
      }
      // Both pins placed — ignore further clicks until Reset
    },

    clearPickup() {
      if (this.pickupMarker) { this.pickupMarker.remove(); this.pickupMarker = null }
      this.pickup          = null
      this.geocodingPickup = false
    },

    clearDestination() {
      if (this.destMarker) { this.destMarker.remove(); this.destMarker = null }
      this.destination   = null
      this.geocodingDest = false
    },

    resetPoints() {
      this.clearPickup()
      this.clearDestination()
    },

    clearForm() {
      this.resetPoints()
      this.form.bookingDateTime    = ""
      this.form.numberOfPassengers = 2
      this.form.cabType            = "Premium"
      this.error                   = ""
      this.message                 = ""
    },

    async createBooking() {
      this.message = ""
      this.error   = ""

      if (!this.pickup || !this.destination) {
        this.error = "Please place both a pickup and a destination pin on the map before booking."
        return
      }

      // Guard against submitting before geocoding finishes
      if (this.geocodingPickup || this.geocodingDest) {
        this.error = "Location names are still loading — please wait a moment and try again."
        return
      }

      try {
        this.loading = true

        await this.$store.dispatch("createBooking", {
          startingLocation:   this.pickup.name,
          endingLocation:     this.destination.name,
          startingLatitude:   this.pickup.lat,
          startingLongitude:  this.pickup.lng,
          endingLatitude:     this.destination.lat,
          endingLongitude:    this.destination.lng,
          bookingDateTime:    new Date(this.form.bookingDateTime).toISOString(),
          numberOfPassengers: this.form.numberOfPassengers,
          cabType:            this.form.cabType,
          userId:             this.$store.getters.userId,
        })

        this.message = "Booking created successfully."
        this.$emit("booking-created")
        this.resetPoints()
        this.form.bookingDateTime    = ""
        this.form.numberOfPassengers = 2
        this.form.cabType            = "Premium"
      } catch (error) {
        this.error = error.response?.data?.message || "Could not create booking"
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.field-label {
  @apply block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide;
}
.field-input {
  @apply w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm bg-slate-50 text-slate-800
         placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500
         focus:border-transparent transition;
}

/* Leaflet tooltip pill labels */
:deep(.cb-tip) {
  background: rgba(15, 23, 42, 0.85);
  border: none;
  border-radius: 5px;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  font-family: 'Montserrat', ui-sans-serif, sans-serif;
  padding: 3px 8px;
  white-space: nowrap;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
}
:deep(.cb-tip::before) {
  display: none;
}
:deep(.cb-tip--blue) {
  background: rgba(37, 99, 235, 0.92);
}
:deep(.cb-tip--green) {
  background: rgba(22, 163, 74, 0.92);
}
</style>
